import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ShieldAlert, Brain, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

const THREAT_LEVELS = [
  { level: 1, label: "LOW", color: "bg-green-500", desc: "Routine monitoring" },
  { level: 2, label: "GUARDED", color: "bg-blue-500", desc: "Elevated awareness" },
  { level: 3, label: "ELEVATED", color: "bg-yellow-500", desc: "Significant risk" },
  { level: 4, label: "HIGH", color: "bg-orange-500", desc: "Active threats" },
  { level: 5, label: "CRITICAL", color: "bg-red-500", desc: "Imminent danger" },
];

const StrategicPosture = () => {
  const [showAnalysis, setShowAnalysis] = useState(false);

  const { data: eventsData } = useQuery({
    queryKey: ["all-events-posture"],
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("events")
        .select("event_type, country, occurred_at")
        .gte("occurred_at", since)
        .order("occurred_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data || [];
    },
    staleTime: 60000,
  });

  const { data: aiData, isLoading: aiLoading, refetch: fetchAi } = useQuery({
    queryKey: ["ai-strategic-brief"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("daily-brief-ai");
      if (error) throw error;
      return data;
    },
    enabled: showAnalysis,
    staleTime: 300000,
  });

  const events = eventsData || [];
  const missileCount = events.filter((e) => e.event_type === "missile").length;
  const droneCount = events.filter((e) => e.event_type === "drone").length;
  const totalEvents = events.length;
  const uniqueCountries = new Set(events.map((e) => e.country).filter(Boolean)).size;

  let threatLevel = 1;
  if (totalEvents > 5) threatLevel = 2;
  if (totalEvents > 15) threatLevel = 3;
  if (totalEvents > 30 || missileCount > 5) threatLevel = 4;
  if (totalEvents > 50 || missileCount > 15) threatLevel = 5;

  const threat = THREAT_LEVELS[threatLevel - 1];

  const regionMap: Record<string, number> = {};
  events.forEach((e) => {
    const r = e.country || "Unknown";
    regionMap[r] = (regionMap[r] || 0) + 1;
  });
  const topRegions = Object.entries(regionMap).sort((a, b) => b[1] - a[1]).slice(0, 8);

  return (
    <section id="strategic-posture" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-red-400" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Global Strategic Posture
          </h2>
          <button
            onClick={() => { setShowAnalysis(true); fetchAi(); }}
            className="ml-auto flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-[10px] font-mono uppercase text-primary hover:bg-primary/20 transition-colors"
          >
            <Brain className="h-3 w-3" />
            AI Analysis
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-[300px_1fr]">
          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <p className="text-[10px] font-mono uppercase text-muted-foreground mb-3">Global Threat Level</p>
            <div className={`mx-auto flex h-24 w-24 items-center justify-center rounded-full ${threat.color} text-white`}>
              <span className="text-3xl font-bold">{threatLevel}</span>
            </div>
            <p className={`mt-3 text-lg font-bold font-mono ${threat.color.replace("bg-", "text-")}`}>{threat.label}</p>
            <p className="text-xs text-muted-foreground mt-1">{threat.desc}</p>
            <div className="mt-4 flex gap-1">
              {THREAT_LEVELS.map((t) => (
                <div key={t.level} className={`h-2 flex-1 rounded-full ${t.color} ${t.level <= threatLevel ? "opacity-100" : "opacity-20"}`} />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Events (24h)" value={totalEvents} />
              <StatCard label="Missiles" value={missileCount} color="text-red-400" />
              <StatCard label="Drones" value={droneCount} color="text-orange-400" />
              <StatCard label="Countries" value={uniqueCountries} />
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <p className="text-[10px] font-mono uppercase text-muted-foreground mb-3">Most Affected (24h)</p>
              <div className="space-y-2">
                {topRegions.map(([country, count]) => {
                  const pct = totalEvents > 0 ? (count / totalEvents) * 100 : 0;
                  return (
                    <div key={country} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-foreground w-12">{country}</span>
                      <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* AI Analysis Panel */}
        {showAnalysis && (
          <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-4 w-4 text-primary" />
              <span className="text-xs font-mono font-bold uppercase text-primary">AI Strategic Analysis</span>
              {aiData?.generated_at && (
                <span className="text-[9px] text-muted-foreground ml-auto">
                  {new Date(aiData.generated_at).toLocaleTimeString()}
                </span>
              )}
            </div>
            {aiLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Generating analysis...
              </div>
            ) : aiData?.analysis ? (
              <div className="text-xs text-foreground/90 whitespace-pre-line leading-relaxed font-mono">
                {aiData.analysis}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Click to generate AI analysis.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const StatCard = ({ label, value, color }: { label: string; value: number; color?: string }) => (
  <div className="rounded-lg border border-border bg-card p-3 text-center">
    <p className={`text-2xl font-bold font-mono ${color || "text-foreground"}`}>{value}</p>
    <p className="text-[10px] font-mono uppercase text-muted-foreground mt-1">{label}</p>
  </div>
);

export default StrategicPosture;
